import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Tab, Nav } from 'react-bootstrap';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import { apiService } from '../../../services/api';
import './WhitelistTemplate.scss';

interface WhitelistField {
  id: number;
  field__name: string;
  field__field_type: string;
  field__required: boolean;
  field__options: string;
  display_status: 'visible' | 'hidden' | 'required';
  order: number;
  custom_label?: string;
  custom_help_text?: string;
  validation_message?: string;
}

interface WhitelistTemplateProps {
  event: {
    id: number;
    title: string;
    slug: string;
    starts: string;
    venue?: {
      name: string;
    };
    setting: {
      whitelist_only: boolean;
      whitelist_message?: string;
      live: boolean;
      status: string;
      currency: {
        symbol: string;
      };
    };
    content: {
      description?: string;
      policy?: string;
      thumbnail_url: string;
      banner_url: string;
    };
    categories: any[];
    whitelist_fields: WhitelistField[];
  };
  user?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  } | null;
}

const WhitelistTemplate: React.FC<WhitelistTemplateProps> = ({ event, user }) => {
  const [activeTab, setActiveTab] = useState('request');
  const [formData, setFormData] = useState({
    name: user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : '',
    email: user?.email || '',
    phone: '',
    create_account: false,
  });
  const [customFields, setCustomFields] = useState<Record<string, any>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCustomFieldChange = (fieldName: string, value: any) => {
    setCustomFields(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleFileChange = (fieldName: string, file: File | null) => {
    setFiles(prev => ({ ...prev, [fieldName]: file }));
  };


  const getOptionsDict = (fieldOptions: string) => {
    if (!fieldOptions) return {};
    try {
      // Options are stored as value:label pairs separated by newlines
      const lines = fieldOptions.split('\n');
      const options: Record<string, string> = {};
      lines.forEach(line => {
        if (line.includes(':')) {
          const [value, label] = line.split(':', 2);
          options[value.trim()] = label.trim();
        }
      });
      return options;
    } catch {
      return {};
    }
  };

  const renderCustomField = (field: WhitelistField) => {
    const fieldName = field.field__name;
    const fieldType = field.field__field_type;
    const fieldRequired = field.field__required;
    const fieldOptions = field.field__options;
    const value = customFields[fieldName] || '';
    const file = files[fieldName];

    const label = field.custom_label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    switch (fieldType) {
      case 'text':
        return (
          <Form.Group key={field.id} className="mb-3">
            <Form.Label className={fieldRequired ? 'required' : ''}>
              {label}
            </Form.Label>
            <Form.Control
              type="text"
              name={fieldName}
              value={value}
              onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
              required={fieldRequired}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            {field.custom_help_text && (
              <Form.Text className="text-muted">{field.custom_help_text}</Form.Text>
            )}
          </Form.Group>
        );

      case 'textarea':
        return (
          <Form.Group key={field.id} className="mb-3">
            <Form.Label className={fieldRequired ? 'required' : ''}>
              {label}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name={fieldName}
              value={value}
              onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
              required={fieldRequired}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            {field.custom_help_text && (
              <Form.Text className="text-muted">{field.custom_help_text}</Form.Text>
            )}
          </Form.Group>
        );

      case 'select':
        const selectOptions = getOptionsDict(fieldOptions);
        return (
          <Form.Group key={field.id} className="mb-3">
            <Form.Label className={fieldRequired ? 'required' : ''}>
              {label}
            </Form.Label>
            <Form.Select
              name={fieldName}
              value={value}
              onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
              required={fieldRequired}
            >
              <option value="">Select {label.toLowerCase()}</option>
              {Object.entries(selectOptions).map(([optionValue, optionLabel]) => (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              ))}
            </Form.Select>
            {field.custom_help_text && (
              <Form.Text className="text-muted">{field.custom_help_text}</Form.Text>
            )}
          </Form.Group>
        );

      case 'radio':
        const radioOptions = getOptionsDict(fieldOptions);
        return (
          <Form.Group key={field.id} className="mb-3">
            <Form.Label className={fieldRequired ? 'required' : ''}>
              {label}
            </Form.Label>
            <div className="radio-group">
              {Object.entries(radioOptions).map(([optionValue, optionLabel]) => (
                <div key={optionValue} className="radio-item">
                  <Form.Check
                    type="radio"
                    name={fieldName}
                    id={`${fieldName}_${optionValue}`}
                    value={optionValue}
                    checked={value === optionValue}
                    onChange={(e) => handleCustomFieldChange(fieldName, e.target.value)}
                    required={fieldRequired}
                    label={optionLabel}
                  />
                </div>
              ))}
            </div>
            {field.custom_help_text && (
              <Form.Text className="text-muted">{field.custom_help_text}</Form.Text>
            )}
          </Form.Group>
        );

      case 'file':
      case 'image':
        return (
          <Form.Group key={field.id} className="mb-3">
            <Form.Label className={fieldRequired ? 'required' : ''}>
              {label}
            </Form.Label>
            <div className="file-input">
              <input
                type="file"
                id={fieldName}
                accept={fieldType === 'image' ? 'image/*' : '*/*'}
                onChange={(e) => handleFileChange(fieldName, e.target.files?.[0] || null)}
                required={fieldRequired}
                style={{ display: 'none' }}
              />
              <label htmlFor={fieldName} className="file-input-label">
                <i className="fas fa-upload"></i>
                <span>
                  {file ? file.name : 'Upload Image'}
                </span>
              </label>
            </div>
            {field.custom_help_text && (
              <Form.Text className="text-muted">{field.custom_help_text}</Form.Text>
            )}
          </Form.Group>
        );

      default:
        return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      
      // Add basic form data
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('create_account', formData.create_account.toString());

      // Add custom field data
      Object.entries(customFields).forEach(([key, value]) => {
        if (value) {
          formDataToSend.append(key, value);
        }
      });

      // Add files
      Object.entries(files).forEach(([key, file]) => {
        if (file) {
          formDataToSend.append(key, file);
        }
      });

      const result = await apiService.whitelist.submitRequest(event.slug, formDataToSend);

      if (result.success) {
        setSubmitStatus('success');
        setSubmitMessage(result.message || 'Your request has been submitted successfully!');
      } else {
        setSubmitStatus('error');
        setSubmitMessage(result.message || 'There was an error submitting your request.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setSubmitMessage('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="whitelist-page">
      <Container>
        <div className="whitelist-container">
          <div className="whitelist-card">
            {/* Event Image Section */}
            <div className="event-image-section">
              <img
                src={event.content.banner_url || event.content.thumbnail_url || '/static/images/event-placeholder.jpg'}
                alt={event.title}
                className="event-banner"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/static/images/event-placeholder.jpg';
                }}
              />
            </div>

            {/* Form Section */}
            <div className="form-section">
              <div className="form-header">
                <h1 className="event-title">{event.title}</h1>
                <h2>Request Access</h2>
                <p>This event requires approval. Please fill out the form below to request access.</p>
              </div>


              {/* Navigation Tabs */}
              <div className="whitelist-nav">
                <button
                  className={`whitelist-nav-button ${activeTab === 'request' ? 'active' : ''}`}
                  onClick={() => setActiveTab('request')}
                >
                  <i className="fas fa-user-plus"></i>
                  Request Access
                </button>
                <button
                  className={`whitelist-nav-button ${activeTab === 'about' ? 'active' : ''}`}
                  onClick={() => setActiveTab('about')}
                >
                  <i className="fas fa-info-circle"></i>
                  About Event
                </button>
              </div>

              {/* Tab Content */}
              <div className="tab-content">
                {activeTab === 'request' && (
                  <div className="tab-pane active">
                    <Form onSubmit={handleSubmit}>
                      {/* Basic Information */}
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">Full Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              required
                              placeholder="Enter your full name"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="required">Email</Form.Label>
                            <Form.Control
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              placeholder="Enter your email"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <InternationalPhoneInput
                          value={formData.phone}
                          onChange={(value) => setFormData(prev => ({ ...prev, phone: value || '' }))}
                          placeholder="Enter your phone number"
                          id="phone"
                          name="phone"
                          label="Phone Number"
                          showLabel={true}
                          required={true}
                        />
                      </Form.Group>

                      {/* Custom Fields */}
                      {event.whitelist_fields.map(renderCustomField)}

                      {/* Create Account Option */}
                      {!user && (
                        <Form.Group className="mb-3 ms-3">
                          <Form.Check
                            type="checkbox"
                            name="create_account"
                            id="create_account"
                            checked={formData.create_account}
                            onChange={handleInputChange}
                            label="Create an account for me"
                          />
                        </Form.Group>
                      )}

                      {/* Submit Button */}
                      <div className="submit-section">
                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          disabled={isSubmitting}
                          className="whitelist-btn whitelist-btn-primary"
                        >
                          {isSubmitting ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane"></i>
                              Submit Request
                            </>
                          )}
                        </Button>
                      </div>

                      {/* Status Messages */}
                      {submitStatus === 'success' && (
                        <Alert variant="success" className="mt-3">
                          <i className="fas fa-check-circle"></i>
                          {submitMessage}
                        </Alert>
                      )}

                      {submitStatus === 'error' && (
                        <Alert variant="danger" className="mt-3">
                          <i className="fas fa-exclamation-circle"></i>
                          {submitMessage}
                        </Alert>
                      )}
                    </Form>
                  </div>
                )}

                {activeTab === 'about' && (
                  <div className="tab-pane active">
                    <div className="about-content">
                      {/* Event Description */}
                      {event.content.description && (
                        <div className="about-section">
                          <h3>About This Event</h3>
                          <div dangerouslySetInnerHTML={{ __html: event.content.description }} />
                        </div>
                      )}

                      {/* Event Categories */}
                      {event.categories && event.categories.length > 0 && (
                        <div className="about-section">
                          <h3>Available Categories</h3>
                          <div className="data-grid">
                            {event.categories
                              .filter((category: any) => !category.hidden)
                              .map((category: any) => (
                                <div key={category.id} className="category-card">
                                  <div className="category-name">{category.name}</div>
                                  <div className="category-price">
                                    {category.price === 0 ? 'Free' : `${event.setting.currency.symbol}${category.price}`}
                                  </div>
                                  {category.description && (
                                    <div className="category-description">{category.description}</div>
                                  )}
                                  {category.remaining !== undefined && (
                                    <div className="category-remaining">
                                      {category.remaining > 0 
                                        ? `${category.remaining} tickets remaining`
                                        : 'Sold out'
                                      }
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Event Policy */}
                      {event.content.policy && (
                        <div className="about-section">
                          <h3>Event Policy</h3>
                          <div dangerouslySetInnerHTML={{ __html: event.content.policy }} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default WhitelistTemplate;
